import { Media, Newsletter, NewsletterContent, NiCode, QuickAddOption, SelectionOption, SupportedTimezone, Ticker, SLDisplayState } from '@/app/lib/types'
import { SocialPlatformMetadata } from '../components/Editor/Nodes/SocialComponent/types'
import { TransitionProps } from '@mantine/core'

import { VIDEO_WATCH_NOW_IMAGE_BY_LANGUAGE } from '@/app/lib/i18n/mediaAssets'
import icon from '@/app/public/icon.svg'
import halloweenIcon from '@/app/public/halloweenIcon.png'
import thanksgivingIcon from '@/app/public/thanksgivingIcon.png'
import christmasIcon from '@/app/public/christmasIcon.png'
import cnyIcon from '@/app/public/cnyIcon.png'
import valentineIcon from '@/app/public/valentineIcon.png'
import prideIcon from '@/app/public/prideIcon.png'
import birthdayIcon from '@/app/public/birthdayIcon.png'

export const SEASONAL_ICON_ASSETS = {
  birthday: birthdayIcon,
  pride: prideIcon,
  cny: cnyIcon,
  valentine: valentineIcon,
  halloween: halloweenIcon,
  thanksgiving: thanksgivingIcon,
  christmas: christmasIcon,
  default: icon,
} as const

export const SEASONAL_MESSAGES = {
  birthday: 'Happy Birthday!',
  pride: 'Happy Pride!',
  cny: 'Happy Chinese New Year!',
  valentine: "Happy Valentine's Day!",
  halloween: 'Happy Halloween!',
  thanksgiving: 'Happy Thanksgiving!',
  christmas: 'Happy Holidays!',
  default: 'Snowy',
} as const

export const IMAGE_FALLBACK = 'https://www.vertasset.com/wp-content/uploads/2022/12/bloomberg-logo-e1693495730549.png'

/** English default; use `getVideoWatchNowImage(language)` when locale is known. */
export const VIDEO_WATCH_NOW_IMAGE = VIDEO_WATCH_NOW_IMAGE_BY_LANGUAGE.en
export const PODCAST_LISTEN_NOW_IMAGE = 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/ihClwsFiKu8o/v0/-1x-1.png'

export const VIDEO_DEFAULT_ALT_TEXT = 'Watch Now'

export const ITEMS_PER_PAGE = 15

export const OPINION_BRAND_SLUG = 'bview'

export const GROUP_PVF_MAP: Record<string, string> = {
  PVF_TINTIN_500: 'news',
  PVF_TINTIN_501: 'opinion',
}

export const USER_DEBUG_STYLES = 'background-color: darkblue; color: white; font-style: italic; border: 5px solid hotpink; font-size: 2em; padding: 4px 8px'
export const NEWSLETTER_DEBUG_STYLES = 'background-color: black; color: white; font-style: italic; border: 5px solid hotpink; font-size: 2em; padding: 4px 8px'
export const NEWSLETTER_CONTENT_DEBUG_STYLES = 'background-color: purple; color: white; font-style: italic; border: 5px solid hotpink; font-size: 2em; padding: 4px 8px'

export const TYPE_TO_BG_COLOUR_CLASS = {
  CAMPAIGN: 'bg-lime-300',
  TEMPLATE: 'bg-orange-300',
  NEWSLETTER: 'bg-blue-300',
}

export const ONE_SECOND = 1000
export const ONE_MINUTE = 60 * ONE_SECOND
export const FIVE_MINUTES = 5 * ONE_MINUTE
export const FIFTEEN_MINUTES = 15 * ONE_MINUTE
export const SIXTEEN_MINUTES = 16 * ONE_MINUTE
export const FORCE_SEND_SHOWN_AFTER = 20 * ONE_MINUTE
export const MARKET_SNAPSHOT_STALE_WARNING = 60 * ONE_MINUTE

export const DEFAULT_NEWSLETTER: Newsletter = {
  id: 0,
  slug: '',
  active: false,
  includeInReporting: true,
  title: '',

  subscriptionType: '',
  language: '',
  description: '',

  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,

  subscriptionEntitlements: [],
  homepageEyebrow: '',
  homepageCardHeadline: '',
  transactionalEmailTitle: '',

  credits: [],
  signUpList: null,
  sampleURL: '',
  podcastUrl: '',
  offerPageUrl: '',
  frequency: '',

  displayTags: [],
  region: '',
  topicTags: [],

  media: {},

  linkTrackingCode: '',
  utmCampaign: '',
}

export const DEFAULT_NEWSLETTER_CONTENT: NewsletterContent = {
  id: 0,
  type: 'CAMPAIGN',
  storyGroupId: null,
  storyRevisionId: null,
  storyWebUrl: null,
  emailCampaignId: null,
  terminalPublishAt: null,
  webPublishAt: null,
  emailPublishAt: null,
  publishState: 'draft',
  publishingError: null,
  name: '',
  headline: '',
  description: '',
  primaryBrand: '',
  content_groups: ['news'],
  emailList: null,
  publishToWeb: false,
  publishToTerminal: false,
  scheduledToPublish: null,
  body: '',
  sentEmailCampaignIds: [],
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: null,
  newsletterId: 0,
  newsletter: null,
  parentId: null,
  seoHeadline: '',
  terminalHeadline: '',
  testEmailList: null,
  fromEmailName: '',
  secondaryBrands: [],
  franchise: '',
  pillar: '',
  eyebrow: '',
  emailPreview: '',
  subjectLine: '',
  editorNote: '',
  credits: [],
  viewership: {},
  media: {},
  social: {},
  magazine: {},
  niCodes: [],
  people: [],
  tickers: [],
  revisionsCount: 0,
  lock: null,
  adOptions: {},
  adTestEmailList: null,
}

export const REMOVE_PUBLISHING_CONTENT: Partial<NewsletterContent> = {
  publishState: 'draft',
  storyGroupId: null,
  storyRevisionId: null,
  storyWebUrl: null,
  emailCampaignId: null,
  publishingError: null,
  terminalPublishAt: null,
  webPublishAt: null,
  emailPublishAt: null,
  sentEmailCampaignIds: [],
}

export const NEWSLETTER_SUBSCRIPTION_OPTIONS: SelectionOption[] = [
  {
    value: 'free',
    label: 'Free',
  },
  {
    value: 'subscriberOnly',
    label: 'Subscriber Only',
  },
]

export const NEWSLETTER_ACTIVE_OPTIONS: SelectionOption[] = [
  {
    value: 'true',
    label: 'Active',
  },
  {
    value: 'false',
    label: 'Inactive',
  },
]

export const NEWSLETTER_FREQUENCY_OPTIONS: SelectionOption[] = [
  {
    value: 'twice-a-day',
    label: 'Twice a Day',
  },
  {
    value: 'daily',
    label: 'Daily',
  },
  {
    value: 'weekly',
    label: 'Weekly',
  },
  {
    value: 'bi-weekly',
    label: 'Bi Weekly',
  },
  {
    value: 'twice-a-week',
    label: 'Twice a Week',
  },
  {
    value: 'monthly',
    label: 'Monthly',
  },
  {
    value: 'as-needed',
    label: 'As Needed',
  },
]

export const NEWSLETTER_LANGUAGES: SelectionOption[] = [
  {
    value: 'en',
    label: 'English',
  },
  {
    value: 'ja',
    label: 'Japanese',
  },
  {
    value: 'de',
    label: 'Deutsch',
  },
  {
    value: 'es',
    label: 'Spanish',
  },
  {
    value: 'pt',
    label: 'Portuguese',
  },
  {
    value: 'zh',
    label: 'Chinese',
  },
  {
    value: 'ko',
    label: 'Korean',
  },
]

export const NEWSLETTER_CONTENT_PUBLISH_STATES: SelectionOption[] = [
  {
    value: 'draft',
    label: 'Draft',
  },
  {
    value: 'published',
    label: 'Published',
  },
  {
    value: 'scheduled',
    label: 'Scheduled',
  },
]

export const NEWSLETTER_CONTENT_MAGAZINE_TYPES: SelectionOption[] = [
  {
    value: 'businessweek',
    label: 'Businessweek',
  },
  {
    value: 'markets-magazine',
    label: 'Markets Magazine',
  },
  {
    value: 'green-magazine',
    label: 'Green Magazine',
  },
]

export const NEWSLETTER_TAGS: SelectionOption[] = [
  {
    value: 'popular',
    label: 'Popular',
  },
  {
    value: 'new',
    label: 'New',
  },
  {
    value: 'bloomberg-uk',
    label: 'Bloomberg UK',
  },
  {
    value: 'bloomberg-opinion',
    label: 'Bloomberg Opinion',
  },
  {
    value: 'coming-soon',
    label: 'Coming Soon',
  },
  {
    value: 'featured',
    label: 'Featured',
  },
]

export const NEWSLETTER_REGIONS: SelectionOption[] = [
  {
    value: 'global',
    label: 'Global',
  },
  {
    value: 'americas',
    label: 'Americas',
  },
  {
    value: 'asia',
    label: 'Asia',
  },
  {
    value: 'europe',
    label: 'Europe',
  },
  {
    value: 'middle-east',
    label: 'Middle East',
  },
]

export const NEWSLETTER_TOPICS: SelectionOption[] = [
  {
    value: 'culture',
    label: 'Culture',
  },
  {
    value: 'economics',
    label: 'Economics',
  },
  {
    value: 'finance',
    label: 'Finance',
  },
  {
    value: 'markets',
    label: 'Markets',
  },
  {
    value: 'media',
    label: 'Media',
  },
  {
    value: 'money',
    label: 'Money',
  },
  {
    value: 'opinion',
    label: 'Opinion',
  },
  {
    value: 'science',
    label: 'Science',
  },
  {
    value: 'sustainability',
    label: 'Sustainability',
  },
  {
    value: 'technology',
    label: 'Technology',
  },
  {
    value: 'politics',
    label: 'Politics',
  },
  {
    value: 'global',
    label: 'Global',
  },
  {
    value: 'news-briefings',
    label: 'News Briefings',
  },
  {
    value: 'cityLab',
    label: 'CityLab',
  },
]

export const NEWSLETTER_CONTENT_CREDIT_ROLES: SelectionOption[] = [
  {
    value: 'assist',
    label: 'Assist',
  },
  {
    value: 'author',
    label: 'Author',
  },
  {
    value: 'contributor',
    label: 'Contributor',
  },
  {
    value: 'editor',
    label: 'Editor',
  },
  {
    value: 'staff',
    label: 'Staff',
  },
  {
    value: 'columnist',
    label: 'Columnist',
  },
  {
    value: 'watcher',
    label: 'Watcher',
  },
  {
    value: 'guest',
    label: 'Guest Columnist',
  },
]

export const MEDIA_UPLOAD_SOURCES: SelectionOption[] = [
  {
    label: 'BN Archives',
    value: 'bn',
  },
  {
    label: 'Wolf',
    value: 'wolf',
  },
  {
    label: 'Toaster',
    value: 'toaster',
  },
  {
    label: 'Terminal G Charts',
    value: 'terminalcharts',
  },
  {
    label: 'AVMM',
    value: 'avmm',
  },
  {
    label: '3rd Party Archives',
    value: 'thirdparty',
  },
  {
    label: 'Sponsored Content',
    value: 'sponsored',
  },
  {
    label: 'Games',
    value: 'games',
  },
]

export const ALL_SOURCES = MEDIA_UPLOAD_SOURCES.map((source) => source.value)

export const BPIX_PERMISSION_IDS = {
  WOLF: 426,
  BUSINESSWEEK: 37,
  TOASTER: [403, 404],
  TERMINAL_CHARTS: [43, 603],
  MEDIA_SOURCE: 5,
  BN: [12, 34, 99],
  THIRD_PARTY: 11,
  AVMM: 6,
  SPONSORED: 207,
  GAMES_SUBMIT_APP_ID: 740,
  GAMES_DAM_PERMISSION_ID: 2457,
}

export const FALLBACK_IMAGE: Media = {
  id: 0,
  type: 'photo',
  url: 'https://placehold.co/300x200/EEE/31343C',
  caption: '',
  credit: '',
  altText: '',
  title: 'Image could not be displayed',
  publishedAt: 1734739200,
}

export const SOCIAL_PLATFORM_METADATA: SocialPlatformMetadata = {
  facebook: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i7G4HOaCfw0A/v0/-1x-1.png',
    label: 'Facebook',
  },
  instagram: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iDRduxloBOSA/v0/-1x-1.png',
    label: 'Instagram',
  },
  x: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i5QE5__h22bE/v0/-1x-1.png',
    label: 'X',
  },
  linkedin: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iiSKUb3JWcLI/v0/-1x-1.png',
    label: 'LinkedIn',
  },
  bluesky: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i_JvbwNnmprk/v0/-1x-1.png',
    label: 'Bluesky',
  },
  whatsapp: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iXt_II64P_EM/v0/-1x-1.png',
    label: 'WhatsApp',
  },
  tiktok: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/izMF6uUluOIo/v0/-1x-1.png',
    label: 'TikTok',
  },
  youtube: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/i8c8M5x09Tig/v0/-1x-1.png',
    label: 'YouTube',
  },
  google: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iCmbzy9VnPpA/v0/-1x-1.png',
    label: 'Google',
  },
  line: {
    iconUrl: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/in25mB8fQDM8/v0/-1x-1.png',
    label: 'LINE',
  },
}

export const QUICK_ADD_NI_CODES: QuickAddOption<NiCode>[] = [
  {
    label: 'Prominent Female Voices',
    id: 'BNSHESAID',
    tooltip: "Add if at least one person interviewed in the story is a woman we've sought out for comment.",
  },
  {
    label: 'Bloomberg News Exclusives',
    id: 'EXCLUSIVE',
    tooltip: 'Add if exclusive Bloomberg content.',
  },
]

// Modal Contants

export const MODAL_TRANSITION: Partial<TransitionProps> = {
  transition: 'fade',
  duration: 150,
  timingFunction: 'linear',
}

export const MEDIA_LIBRARY_MODAL_PROPS = {
  size: 'auto',
  centered: true,
  withCloseButton: false,
}

export const STORY_LIST_LIMIT = 10

export const STORY_LIBRARY_MODAL_PROPS = {
  size: 'auto',
  centered: true,
  withCloseButton: false,
}

export const SNIPPET_LIBRARY_MODAL_PROPS = {
  size: 'auto',
  centered: true,
  withCloseButton: false,
}

export const PODCAST_LIBRARY_MODAL_PROPS = {
  size: 'auto',
  centered: true,
  withCloseButton: false,
}

export const TEST_EMAIL_MODAL_PROPS = {
  size: 600,
  centered: true,
  withCloseButton: false,
}

export const TERMINAL_PREVIEW_MODAL_PROPS = {
  centered: true,
  withCloseButton: false,
}

export const PUBLISH_MODAL_PROPS = {
  size: '60%',
  centered: true,
  withCloseButton: false,
  radius: 'md',
  styles: {
    body: { padding: 0 },
  },
}

export const SCHEDULE_WARNING_MODAL_PROPS = {
  size: 600,
  withCloseButton: false,
}

export const IMPORT_STORY_CONTENT_MODAL_PROPS = {
  size: 600,
  centered: true,
  withCloseButton: false,
}

/*
  Tintin PVFs:
  1: Prod Admin
  2: Stg Admin
  100: Newsletter Read
  101: Newsletter Update
  102: Newsletter Create
  200: Template Read
  201: Template Update
  202: Template Create
  300: Campaign Read
  301: Campaign Update
  302: Campaign Create
  304: Campaign Publish
  401: Ads
  402: Social
  403: Publish
  Scopes/Memberships
  501: Business
  502: Opinion
*/

// For testing purposes
export const TEST_PVF_CONFIGS = {
  admin: ['PVF_TINTIN_1'],
  readOnly: ['PVF_TINTIN_100', 'PVF_TINTIN_200', 'PVF_TINTIN_300'],
  readEditOnly: ['PVF_TINTIN_100', 'PVF_TINTIN_101', 'PVF_TINTIN_200', 'PVF_TINTIN_201', 'PVF_TINTIN_300', 'PVF_TINTIN_301'],
  newsletterManager: [
    'PVF_TINTIN_100',
    'PVF_TINTIN_101',
    'PVF_TINTIN_102',
    'PVF_TINTIN_200',
    'PVF_TINTIN_201',
    'PVF_TINTIN_202',
    'PVF_TINTIN_300',
    'PVF_TINTIN_301',
    'PVF_TINTIN_302',
    'PVF_TINTIN_304',
  ],
  newsletterEditor: ['PVF_TINTIN_300', 'PVF_TINTIN_301', 'PVF_TINTIN_302', 'PVF_TINTIN_304', 'PVF_TINTIN_500'],
  newsletterOpinionEditor: ['PVF_TINTIN_300', 'PVF_TINTIN_301', 'PVF_TINTIN_302', 'PVF_TINTIN_304', 'PVF_TINTIN_501'],
  newsletterWriter: ['PVF_TINTIN_300', 'PVF_TINTIN_301', 'PVF_TINTIN_302'],
}

export const AUTOSAVE_INTERVAL = 1000 * 15 // 15 seconds

export const REJECT_NI_CODES = new Set([
  'WEBBLOCK',
  'ONWEB',
  'GOWEB',
  'BBIZ',
  'BPOL',
  'BVIEW',
  'WEBCMMDTZD',
  'WEBLIFESTY',
  'WEBSCOOP',
  'WEBMMSCOOP',
  'WEBFEATURE',
  'WEBLOCKUP',
  'WEBBLOCK',
  'WEBNLONLY',
  'WEBCOJP',
  'WEBNONBBG',
  'WEBLETT',
])

export const WORKFLOWS = {
  PUBLISH: 'PUBLISH_WORKFLOW',
} as const

export const SAVE_TYPES = {
  SAVE: 'Save',
  AUTOSAVE: 'Autosave',
  IMPORT: 'Import Content',
  REVERT: 'Revert',
  PRE_PUBLISH: 'Pre Publish',
  STORY_PUBLISH: 'Story Publish',
  EMAIL_PUBLISH: 'Email Publish',
  WEB_PUBLISH: 'Web Publish',
  WEB_UPDATE: 'Web URL Update',
} as const

export const NEWSLETTER_CONTENT_METADATA_SECTION_MAP: Record<string, string[]> = {
  titleOptions: ['name', 'headline', 'terminalHeadline', 'subjectLine', 'emailPreview'],
  branding: ['primaryBrand', 'eyebrow'],
  emailConfig: ['emailList', 'testEmailList', 'fromEmailName'],
  media: ['media.lede'],
}

export const TIMEZONE_MAP: Record<string, string> = {
  'New York': 'America/New_York',
  London: 'Europe/London',
  'Hong Kong': 'Asia/Hong_Kong',
  Beijing: 'Asia/Shanghai',
  Tokyo: 'Asia/Tokyo',
  Seoul: 'Asia/Seoul',
  Mumbai: 'Asia/Kolkata',
  Dubai: 'Asia/Dubai',
  Sydney: 'Australia/Sydney',
  Johannesburg: 'Africa/Johannesburg',
  Berlin: 'Europe/Berlin',
  'Mexico City': 'America/Mexico_City',
  'São Paulo': 'America/Sao_Paulo',
}

export const TIMEZONE_ABBREVIATIONS: Record<SupportedTimezone, string> = {
  'New York': 'ET',
  London: 'GMT',
  'Hong Kong': 'HKT',
  Beijing: 'CST',
  Tokyo: 'JST',
  Seoul: 'KST',
  Mumbai: 'IST',
  Dubai: 'GST',
  Sydney: 'AEDT',
  Johannesburg: 'SAST',
  Berlin: 'CET',
  'Mexico City': 'CT',
  'São Paulo': 'BRT',
}

export const TIMESTAMP_FORMAT_OPTIONS = {
  'time-only': 'Time only',
  'date-time': 'Date & time',
} as const

export const TIMESTAMP_SUFFIX_OPTIONS = {
  india: ', or the previous close for Indian markets.',
} as const

export const QUICK_ADD_TICKERS: Ticker[] = [
  {
    id: 'ES1:IND',
    ticker: 'ES1:IND',
    type: 'Index',
    label: 'S&P 500 futures',
    name: 'S&P 500 Futures',
    url: 'https://www.bloomberg.com/quote/ES1:IND',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'NQ1:IND',
    ticker: 'NQ1:IND',
    type: 'Index',
    label: 'Nasdaq 100 futures',
    name: 'Nasdaq 100 Futures',
    url: 'https://www.bloomberg.com/quote/NQ1:IND',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'SXXP:IND',
    ticker: 'SXXP:IND',
    type: 'Index',
    label: 'Stoxx Europe 600 Index',
    name: 'Stoxx Europe 600 Index',
    url: 'https://www.bloomberg.com/quote/SXXP:IND',
    country: 'EU',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'NKY:IND',
    ticker: 'NKY:IND',
    type: 'Index',
    label: 'Nikkei 225',
    name: 'Nikkei 225',
    url: 'https://www.bloomberg.com/quote/NKY:IND',
    country: 'JP',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'HSI:IND',
    ticker: 'HSI:IND',
    type: 'Index',
    label: 'Hang Seng Index',
    name: 'Hang Seng Index',
    url: 'https://www.bloomberg.com/quote/HSI:IND',
    country: 'HK',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'SHSZ300:IND',
    ticker: 'SHSZ300:IND',
    type: 'Index',
    label: 'China CSI 300 Index',
    name: 'Shanghai Shenzhen CSI 300 Index',
    url: 'https://www.bloomberg.com/quote/SHSZ300:IND',
    country: 'CN',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'BBDXY:IND',
    ticker: 'BBDXY:IND',
    type: 'Index',
    label: 'Bloomberg Dollar Spot Index',
    name: 'Bloomberg Dollar Spot Index',
    url: 'https://www.bloomberg.com/quote/BBDXY:IND',
    country: 'EU',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'GT10:GOV',
    ticker: 'GT10:GOV',
    type: 'Bond',
    label: 'US 10-year Treasury yield',
    name: 'US 10-Year Treasury Yield',
    url: 'https://www.bloomberg.com/quote/GT10:GOV',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '',
    priceSuffix: '%',
    pricePrecision: 2,
    changeSuffix: '',
    changePrecision: 2,
  },
  {
    id: 'EURUSD:CUR',
    ticker: 'EURUSD:CUR',
    type: 'Currency',
    label: 'Euro',
    name: 'Euro',
    url: 'https://www.bloomberg.com/quote/EURUSD:CUR',
    country: 'EU',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'USDJPY:CUR',
    ticker: 'USDJPY:CUR',
    type: 'Currency',
    label: 'Dollar-yen',
    name: 'Yen',
    url: 'https://www.bloomberg.com/quote/USDJPY:CUR',
    country: 'JP',
    price: 0,
    change: 0,
    pricePrefix: '¥',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'XBTUSD:CUR',
    ticker: 'XBTUSD:CUR',
    type: 'Currency',
    label: 'Bitcoin',
    name: 'XBTUSD Spot Exchange Rate - Price of 1 XBT in USD',
    url: 'https://www.bloomberg.com/quote/XBTUSD:CUR',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'XAUUSD:CUR',
    ticker: 'XAUUSD:CUR',
    type: 'Currency',
    label: 'Spot gold',
    name: 'XAUUSD Spot Exchange Rate - Price of 1 XAU in USD',
    url: 'https://www.bloomberg.com/quote/XAUUSD:CUR',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'GBPUSD:CUR',
    ticker: 'GBPUSD:CUR',
    type: 'Currency',
    label: 'Pound',
    name: 'GBPUSD Spot Exchange Rate - Price of 1 GBP in USD',
    url: 'https://www.bloomberg.com/quote/GBPUSD:CUR',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'CL1:COM',
    ticker: 'CL1:COM',
    type: 'Commodity',
    label: 'WTI crude oil futures',
    name: 'WTI Crude Oil Futures',
    url: 'https://www.bloomberg.com/quote/CL1:COM',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
  {
    id: 'HG1:COM',
    ticker: 'HG1:COM',
    type: 'Commodity',
    label: 'Copper futures',
    name: "Generic 1st 'HG' Future",
    url: 'https://www.bloomberg.com/quote/HG1:COM',
    country: 'US',
    price: 0,
    change: 0,
    pricePrefix: '$',
    priceSuffix: '',
    pricePrecision: 2,
    changeSuffix: '%',
    changePrecision: 1,
  },
]

export const EDITORIAL_CURRENCY_SYMBOLS: Record<string, string> = {
  CHF: 'Fr', // Swiss Franc
  CAD: 'C$', // Canadian Dollar
  AUD: 'AU$', // Australian Dollar
  NZD: 'NZ$', // New Zealand Dollar
  HKD: 'HK$', // Hong Kong Dollar
  SGD: 'S$', // Singapore Dollar
  MXN: 'MX$', // Mexican Peso
  TWD: 'NT$', // Taiwan Dollar
}

export const MS_TICKER_LIMIT = 7
export const MS_DISCLAIMER = 'Data is subject to provider delays.'
export const MS_ROW_CLASS = 'tintin-ms-row'
export const MS_CELL_CLASS = 'tintin-ms-cell'
export const MS_SECURITY_CLASS = 'tintin-ms-security'
export const MS_PRICE_CLASS = 'tintin-ms-price'
export const MS_CHANGE_CLASS = 'tintin-ms-change'
export const MS_CHANGE_POSITIVE_CLASS = 'tintin-ms-change-positive'
export const MS_CHANGE_NEGATIVE_CLASS = 'tintin-ms-change-negative'
export const MS_CHANGE_NEUTRAL_CLASS = 'tintin-ms-change-neutral'

export const NEWSLETTER_SUBSCRIPTION_ENTITLEMENTS = [
  {
    label: 'Technology',
    value: 'TECH_NEWSLETTER',
  },
]

export const STOP_WORDS = [
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'for',
  'if',
  'in',
  'into',
  'is',
  'it',
  'no',
  'not',
  'of',
  'on',
  'or',
  'such',
  'that',
  'the',
  'their',
  'then',
  'there',
  'these',
  'they',
  'this',
  'to',
  'was',
  'will',
  'with',
]

export type GameId = 'alphadots' | 'pointed'

// The game's icon only — no wordmark or "Bloomberg Games", both of which are rendered as text
// (see GameConfig.heading and the literal "Bloomberg Games" in the templates). The Figma lockup
// outlines everything except the word "Games" to vector; this departs from that deliberately, so
// the wordmark and "Bloomberg Games" render at one guaranteed size instead of whatever a given
// export's transparent padding happens to produce. `width`/`height` are DISPLAY dimensions,
// set to the mark's Figma height (Alphadots 25, Pointed 40) with the width following each
// asset's own aspect, which differs slightly from the Figma vector's.
export interface GameMark {
  url: string
  width: number
  height: number
}

export interface GameConfig {
  url: string
  heading: string
  ctaText: string
  // CTA copy for Terminal/Web rendering. Email always uses `ctaText`; the
  // lower-fidelity Terminal/Web fallback uses this when set, else falls back to `ctaText`.
  terminalCtaText?: string
  // Tagline shown under the heading in Terminal/Web rendering only (never in email).
  tagline?: string
  prompt?: string
  label?: string
  mark: GameMark
  // Divider between the wordmark and "Bloomberg Games", per Figma.
  dividerColor: string
  // When set, the CTA deep-links to the archive entry for the puzzle the tout was built
  // from; otherwise it always points at the game's landing page.
  linksToArchive?: boolean
}

export const GAMES_TOUT_CONFIG: Record<GameId, GameConfig> = {
  alphadots: {
    url: 'https://www.bloomberg.com/games/alphadots',
    heading: 'Alphadots',
    ctaText: 'Play',
    terminalCtaText: 'Play Alphadots',
    tagline: 'The word puzzle with a plot twist',
    label: "Today's clue is:",
    mark: {
      url: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/il8cqH8TDZCQ/v0/-1x-1.png',
      width: 47,
      height: 25,
    },
    dividerColor: '#78aaad',
    linksToArchive: true,
  },
  pointed: {
    url: 'https://www.bloomberg.com/games/pointed',
    heading: 'Pointed',
    ctaText: 'Play',
    terminalCtaText: 'Test your knowledge',
    tagline: 'The news quiz for risk-takers',
    prompt: 'Think you know the answer?',
    mark: {
      url: 'https://assets.bwbx.io/images/users/iqjWHBFdfxIU/iZVPhoyqaf2M/v0/-1x-1.png',
      width: 33,
      height: 40,
    },
    dividerColor: '#999999',
  },
}

export const GAMES_TOUT_MODAL_PROPS = {
  size: 400,
  centered: true,
  withCloseButton: true,
  title: 'Select a Game',
}

export const CTA_DEFAULT_TEXT = 'Read more'

export const CTA_OPTIONS = [
  'Click for playback',
  'Follow live coverage',
  'Get full coverage',
  'Get more',
  'Get the latest',
  'Get the newsletter',
  'Keep reading',
  'Listen here',
  'Listen to the podcast',
  'Read more',
  'Read the exclusive',
  'Read the live blog',
  'Read the newsletter',
  'Read the Q&A',
  'Read the story',
  'Register here',
  'See more',
  'See the data',
  'See the graphics',
  'See the poll',
  'See the results',
  'Subscribe',
  'Take the survey',
  'Watch live',
  'Watch the interview',
  'Watch the video',
]

export const STORY_CALLOUT_DISPLAY_OPTIONS = [
  { key: 'borderTop', label: 'Border top' },
  { key: 'image', label: 'Image' },
  { key: 'label', label: 'Label' },
  { key: 'summary', label: 'Summary' },
  { key: 'button', label: 'CTA button' },
  { key: 'borderBottom', label: 'Border bottom' },
] as const

export const DEFAULT_STORY_CALLOUT_DISPLAY_STATE = {
  image: true,
  label: true,
  title: true,
  summary: true,
  button: true,
  borderTop: false,
  borderBottom: false,
} as const

export const SL_DISPLAY_OPTIONS = [
  { key: 'borderTop', label: 'Border top' },
  { key: 'borderBottom', label: 'Border bottom' },
  { key: 'label', label: 'Label' },
  { key: 'summary', label: 'Summary' },
  { key: 'image', label: 'Image' },
] as const

export const DEFAULT_SL_DISPLAY_STATE: SLDisplayState = {
  borderTop: false,
  borderBottom: false,
  label: true,
  summary: true,
  image: true,
  stories: [],
}
